// src/components/games/GameCard.js
import React from 'react';
import { Link } from 'react-router-dom';
import { getAsset } from '../../utils/imageMap';
import { slugFromGameKey } from '../../utils/gameSlugs';
import StarRatingReadOnly from './StarRatingReadOnly';

function pickCover({ icon, key, name, heroUrl, coverImage }) {
  // 🎯 1. Zuerst direkte URLs prüfen (für neue API-Games)
  if (heroUrl && (heroUrl.startsWith('http') || heroUrl.startsWith('/api/'))) {
    return heroUrl;
  }
  if (coverImage && (coverImage.startsWith('http') || coverImage.startsWith('/api/'))) {
    return coverImage;
  }

  // 🎯 2. Dann statische Assets aus imageMap (für alte Games)
  const candidates = [];
  if (icon) candidates.push(icon);
  if (key) candidates.push(`${key}-cover.webp`, `${key}.webp`, `${key}.jpg`, `${key}.png`);
  if (name) {
    const n = String(name).toLowerCase().replace(/\s+/g, '-');
    candidates.push(`${n}-cover.webp`, `${n}.webp`, `${n}.jpg`, `${n}.png`);
  }
  candidates.push('hero-background.webp');
  
  for (const c of candidates) {
    const r = getAsset(c);
    if (r) return r;
  }
  
  return getAsset('hero-background.webp'); // Always return fallback
}

export default function GameCard({ game }) {
  const { name, icon, key, shortDescription, platforms = [], genres = [], heroUrl, coverImage } = game || {};
  const slug = slugFromGameKey(key || name);
  const cover = pickCover({ icon, key, name, heroUrl, coverImage });
  const platformBadge = platforms.slice(0, 3);

  return (
    <div className="game-card">
      <div className="game-card-cover">
        {cover ? (
          <img
            src={cover.src || cover}
            srcSet={Array.isArray(cover.srcSet) ? cover.srcSet.join(', ') : (game.coverSrcset || '').trim() || undefined}
            sizes="(min-width: 1100px) 33vw, (min-width: 640px) 50vw, 100vw"
            alt={`${name} cover`}
            loading="lazy"
            decoding="async"
            onError={(e) => {
              console.warn('Game cover failed to load:', cover);
              const fallback = getAsset('hero-background.webp');
              if (e.target.src !== fallback) {
                e.target.src = fallback;
              } else {
                e.target.style.display = 'none';
                e.target.parentElement?.classList.add('image-failed');
              }
            }}
            onLoad={(e) => {
              e.target.style.opacity = '1';
              e.target.classList.add('loaded');
            }}
            style={{ opacity: 0, transition: 'opacity 0.3s ease' }}
          />
        ) : (
          <div className="fallback" aria-label="Image not available" />
        )}
        <div className="badges">
          {platformBadge.map((p) => (<span className="badge" key={p}>{p}</span>))}
          {genres.slice(0, 1).map((g) => (<span className="badge" key={g}>{g}</span>))}
        </div>
      </div>
      <div className="game-card-body">
        <div className="card-topline">
          <div className="game-name">{name}</div>
          <StarRatingReadOnly gameKey={key || name} />
        </div>
        <div className="game-desc">{shortDescription || 'No description available.'}</div>
        <div className="card-actions">
          <Link className="va-btn primary" to={`/games/${slug}`}>View details</Link>
          <a 
            className="va-btn ghost" 
            href="https://t.me/Versusarenaentry_bot" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Play now
          </a>
        </div>
      </div>
    </div>
  );
}
